import React from 'react'
import Button from '@mui/material/Button'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import Chip from '@mui/material/Chip'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import { Link, router } from '@inertiajs/react'
import { SidebarLayout } from '@/components/layouts/SidebarLayout'

interface WebhookEvent {
  id: number
  webhook_id: number
  webhook_name: string
  status: string
  status_color: string
  status_label: string
  ip_address: string | null
  content_type: string | null
  created_at: string
}

interface WebhookOption {
  id: number
  name: string
}

interface Filters {
  webhook_id: string | null
  status: string | null
}

interface Props {
  events: WebhookEvent[]
  webhooks: WebhookOption[]
  filters: Filters
}

export default function Index({ events, webhooks, filters }: Props) {
  function handleFilterChange(key: string, value: string) {
    const params: Record<string, string> = {}
    if (key === 'webhook_id' && value) params.webhook_id = value
    else if (filters.webhook_id) params.webhook_id = filters.webhook_id
    if (key === 'status' && value) params.status = value
    else if (filters.status) params.status = filters.status

    if (key === 'webhook_id' && !value) delete params.webhook_id
    if (key === 'status' && !value) delete params.status

    router.get('/monitoring/webhook_events', params, { preserveState: true })
  }

  return (
    <SidebarLayout
      section="monitoring"
      title="Webhook Events"
      action={
        <Button component={Link} href="/manage/webhooks/webhooks" variant="outlined">
          Manage webhooks
        </Button>
      }
    >
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Webhook</InputLabel>
          <Select
            value={filters.webhook_id || ''}
            label="Webhook"
            onChange={e => handleFilterChange('webhook_id', e.target.value as string)}
          >
            <MenuItem value="">All webhooks</MenuItem>
            {webhooks.map(w => (
              <MenuItem key={w.id} value={String(w.id)}>{w.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={filters.status || ''}
            label="Status"
            onChange={e => handleFilterChange('status', e.target.value as string)}
          >
            <MenuItem value="">All statuses</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="processing">Processing</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
            <MenuItem value="failed">Failed</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', overflowX: 'auto' }}>
        <Table sx={{ minWidth: 700 }}>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Webhook</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>IP Address</TableCell>
              <TableCell>Content-Type</TableCell>
              <TableCell>Received</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {events.map((event) => (
              <TableRow key={event.id}>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    #{event.id}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>
                    {event.webhook_name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={event.status_label}
                    size="small"
                    sx={{
                      backgroundColor: event.status_color,
                      color: '#fff',
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                    {event.ip_address || '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {event.content_type || '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {new Date(event.created_at).toLocaleString()}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Button
                    component={Link}
                    href={`/monitoring/webhook_events/${event.id}`}
                    size="small"
                  >
                    View
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {events.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                    No events found.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </SidebarLayout>
  )
}
